<script setup lang="ts">
  import { ChatBubbleBottomCenterTextIcon, DocumentTextIcon } from '@heroicons/vue/24/outline'
  import { TableCell } from '@/components/ui/table'
  import { type CustomerDataMapper as DataMapper } from '@/types/generated/types.gen'
  import { ChangeSourceEnum, AugmentationVariantEnum } from '@/types/generated/types.gen'
  import { computed, defineProps, type PropType, ref } from 'vue'
  import { usePDF, VuePDF } from '@tato30/vue-pdf'
  import { Button } from '@/components/ui/button'
  import { useThemeColor } from '@/composables/useThemeColor'
  import PDFViewer from '@/components/features/pdf/pdfViewer.vue'
  import {
    PencilIcon,
    FileQuestionIcon,
    CalendarIcon,
    FileTextIcon,
    MailIcon,
    PhoneIcon,
    MessageCircleQuestionIcon,
    MessageSquareIcon
  } from 'lucide-vue-next'

  const props = defineProps({
    mapper: {
      type: Object as PropType<DataMapper>,
      required: true
    }
  })

  const showPdfViewer = ref(false)
  const pdfUrl = ref('')
  const currentPage = ref(1)
  const totalPages = ref(1)
  const currentDocumentName = ref('')
  const isLoading = ref(true)
  const highlightedText = ref<string>('Der')
  const themeColor = computed(() => useThemeColor().value)
  const highlightOptions = computed(() => ({
    ignoreCase: true,
    completeWords: true
  }))

  const { pdf, pages } = usePDF(pdfUrl)

  function handlePdfClick(url: string, documentName: string, page: number, data: string) {
    console.log('data', data)
    pdfUrl.value = url
    currentDocumentName.value = documentName
    showPdfViewer.value = true
    isLoading.value = true
    currentPage.value = page
    highlightedText.value = data
  }

  function closePdfViewer() {
    showPdfViewer.value = false
    pdfUrl.value = ''
    currentPage.value = 1
    totalPages.value = 1
    isLoading.value = true
  }

  function onPdfLoaded(pdfInstance: any) {
    isLoading.value = false
    totalPages.value = pdfInstance.numPages
  }

  const updateCurrentPage = (newPage: number) => {
    console.log('newPage', newPage)
    currentPage.value = newPage
  }

  const onHighlight = (event: any) => {
    console.log('event', event)
  }
</script>
<template>
  <TableCell>
    <div v-if="mapper.latest_change">
      <span
        class="flex inline-flex items-center gap-2"
        v-if="mapper.latest_change.change_source === ChangeSourceEnum.USER"
      >
        <PencilIcon class="h-4 w-4" />
        Manuelle Änderung
      </span>
      <span
        class="flex inline-flex items-center gap-2"
        v-else-if="mapper.latest_change.change_source === ChangeSourceEnum.QUESTIONNAIRE"
      >
        <MessageCircleQuestionIcon class="h-4 w-4" />
        Fragebogen
      </span>
      <span
        class="flex inline-flex items-center gap-2"
        v-else-if="mapper.latest_change.change_source === ChangeSourceEnum.APPOINTMENT"
      >
        <CalendarIcon class="h-4 w-4" />
        Terminbuchung
      </span>
      <span
        class="flex inline-flex items-center gap-2"
        v-else-if="mapper.latest_change.change_source === ChangeSourceEnum.AI"
      >
        <span
          class="flex inline-flex items-center gap-2"
          v-if="
            mapper.latest_change.ai_augmentation.augmentation_variant ===
            AugmentationVariantEnum.DOCUMENT_ANALYSIS
          "
        >
          <FileTextIcon class="h-4 w-4" />
          <a
            class="inline-flex cursor-pointer items-center space-x-1 text-purple-700"
            @click="
              handlePdfClick(
                mapper.latest_change.ai_augmentation.document.url,
                mapper.latest_change.ai_augmentation.document.filename,
                1,
                mapper.data as string
              )
            "
          >
            <span>
              <span v-if="mapper.latest_change.ai_augmentation.document.filename.length > 20">
                {{ mapper.latest_change.ai_augmentation.document.filename.slice(0, 16) }}[…].pdf
              </span>
              <span v-else>
                {{ mapper.latest_change.ai_augmentation.document.filename }}
              </span>
              <span
                v-if="mapper.latest_change.ai_augmentation.referenced_document_pages.length == 1"
              >
                Seite {{ mapper.latest_change.ai_augmentation.referenced_document_pages[0] }}
              </span>
              <span
                v-else-if="
                  mapper.latest_change.ai_augmentation.referenced_document_pages.length > 1
                "
              >
                Seiten
                {{ mapper.latest_change.ai_augmentation.referenced_document_pages.join(', ') }}
              </span>
            </span>
          </a>
        </span>
        <span
          class="flex inline-flex items-center gap-2"
          v-else-if="
            mapper.latest_change.ai_augmentation.augmentation_variant ===
            AugmentationVariantEnum.EMAIL_EXTRACTION
          "
        >
          <MailIcon class="h-4 w-4" />
          E-Mail
        </span>
        <span
          class="flex inline-flex items-center gap-2"
          v-else-if="
            mapper.latest_change.ai_augmentation.augmentation_variant ===
            AugmentationVariantEnum.CALL
          "
        >
          <PhoneIcon class="h-4 w-4" />
          Anruf
        </span>
        <span
          class="flex inline-flex items-center gap-2"
          v-else-if="
            mapper.latest_change.ai_augmentation.augmentation_variant ===
            AugmentationVariantEnum.CHATBOT
          "
        >
          <MessageSquareIcon class="h-4 w-4" />
          Chatbot
        </span>
        <span
          v-if="mapper.latest_change.ai_augmentation"
          class="badge bg-purple"
        >
          KI
        </span>
      </span>
      <span v-else-if="mapper.latest_change.change_source === ChangeSourceEnum.SYSTEM">SYSTEM</span>
    </div>
  </TableCell>
  <!-- PDF Viewer Modal -->
  <PDFViewer
    v-model:isOpen="showPdfViewer"
    v-model:url="pdfUrl"
    :filename="currentDocumentName"
    :highlight-text="highlightedText"
    :initial-page="1"
    @loaded="onPdfLoaded"
  />
</template>
